import { useEffect, useCallback } from 'react';

import {
  TChildrenOptions,
  TFormValues,
  THookReturn,
  TUseFormProps,
} from '../react/types';

import { CoreEvents, ALL_NAMESPACE_EVENTS } from '@core';
import { DEFAULT_FORM_HOOK_ID, EEVents } from '@core/constants';
import { getFormInstance } from '@core/managers';
import { getScopedField } from '@core/utils/common';

/**
 * This hooks lets you connect to your form/s in anywhere in your application. Even if you are outside the <FormProvider />
 *
 * You can connect to:
 * - A specific form
 * - Several forms identified by their id's
 * - A group of forms
 *
 */
const useForm = ({
  onValid,
  onData,
  onClick,
  onSubmit,
  id = DEFAULT_FORM_HOOK_ID,
  ids,
}: TUseFormProps): THookReturn => {
  /**
   * Subscribe to some event.
   *
   * If the hook has more than one formId, will run subscription for all of them
   *
   * @param event The event name
   * @param cb The callback to run on subscription
   */
  const subscribe = (event, cb) => {
    if (!cb) return;
    if (ids) {
      ids.map((formId) =>
        getFormInstance(formId).subscribe(event, ({ data }) =>
          cb(getFormInstance(formId).formData, { field: data.field }),
        ),
      );
      return;
    }

    getFormInstance(id).subscribe(event, cb);
  };

  /**
   * Get all the available form data
   *
   * Will return to a given form, or data to multiple form in the same data structure
   *
   * @returns form data
   */
  const extractFormsData = (opts?: TChildrenOptions) => {
    const formData = !ids
      ? getFormInstance(id).formData
      : (ids.reduce(
          (acc, formId) =>
            Object.keys(acc).reduce(
              (innerAcc, key) => ({
                ...innerAcc,
                [key]: {
                  ...acc[key],
                  ...getFormInstance(formId).formData[key],
                },
              }),
              {},
            ),
          {
            formatted: {},
            erroredFields: {},
            fields: {},
            form: {},
            predictableErroredFields: {},
            filteredFields: {},
          },
        ) as TFormValues);
    if (
      opts &&
      Object.keys(opts).length &&
      (opts.scopeBlurredChildren ||
        opts.scopeChangedChildren ||
        opts.childrenScope)
    ) {
      const keys = Object.keys(formData.formatted).filter((key) => {
        return (
          (opts?.scopeBlurredChildren && formData.fields[key].blured) ||
          (opts?.scopeChangedChildren && formData.fields[key].changed) ||
          opts?.childrenScope?.includes(key)
        );
      });
      formData.formatted = getScopedField(formData.formatted, keys);
    }

    return formData;
  };

  const onDataChange = useCallback(() => {
    let data = getFormInstance(id).formData;
    if (ids) {
      data = ids.reduce(
        (acc, formId) => ({
          ...acc,
          [formId]: getFormInstance(formId).formData,
        }),
        {},
      ) as TFormValues;
    }

    onData && onData(data);
  }, [onData, onValid]);

  useEffect(
    () => subscribe(EEVents.ON_FIELD_VALIDATION_TOGGLE, onValid),
    [onValid],
  );
  useEffect(
    () => subscribe(EEVents.ON_FIELD_CHANGE, onDataChange),
    [onData, onValid],
  );
  useEffect(
    () =>
      subscribe(
        ALL_NAMESPACE_EVENTS(CoreEvents.ON_FIELD_REHYDRATE),
        onDataChange,
      ),
    [onData, onValid],
  );
  useEffect(
    () => subscribe(CoreEvents.ON_FORM_DESTROY_FIELD, onDataChange),
    [onData, onValid],
  );
  useEffect(
    () =>
      subscribe(
        ALL_NAMESPACE_EVENTS(CoreEvents.ON_FIELD_CLICK),
        () => onClick && onClick(extractFormsData()),
      ),
    [onClick],
  );

  useEffect(
    () =>
      subscribe(CoreEvents.ON_FORM_SUBMIT, () => {
        onSubmit && onSubmit(extractFormsData());
      }),
    [onSubmit],
  );

  const submitForm = () =>
    getFormInstance(ids ? ids[0] : id).publish(CoreEvents.ON_FORM_SUBMIT);

  return { submitForm, formData: extractFormsData };
};

export default useForm;
